/**
 * Google Consent Mode v2 integration.
 *
 * Two moving pieces:
 *   1. `CONSENT_MODE_INIT_SCRIPT` — a raw JS string we inject inline into the
 *      document `<head>` *before* any other script. It initialises `dataLayer`,
 *      defines a `gtag()` helper and calls `gtag('consent', 'default', ...)`
 *      so Google's tags observe the default (denied) signal state BEFORE GTM
 *      or GA4 ever load.
 *   2. `updateConsent(value)` — client-side helper invoked when the user
 *      clicks Accept or Reject in the cookie banner. It calls
 *      `gtag('consent', 'update', ...)` with the full seven-signal record.
 *
 * `functionality_storage` and `security_storage` are always `granted` (they
 * cover strictly-necessary site operation and security features such as
 * CSRF / fraud prevention, which do not require user consent under GDPR).
 * Every other signal mirrors the user's decision.
 */

/** Strict, discriminated user decision captured by the banner. */
export type ConsentValue = 'granted' | 'denied';

/** The full seven-signal record passed to `gtag('consent', ...)`. */
export interface ConsentSignal {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  analytics_storage: ConsentValue;
  functionality_storage: 'granted';
  personalization_storage: ConsentValue;
  security_storage: 'granted';
}

/**
 * Inline JS string that sets the Consent Mode v2 defaults before GTM loads.
 *
 * Must be emitted in `<head>` ahead of every other script tag. Safe to inline
 * because:
 *   - no external requests
 *   - no user-controlled data interpolated
 *   - does not throw on environments without `dataLayer`
 */
export const CONSENT_MODE_INIT_SCRIPT = [
  'window.dataLayer=window.dataLayer||[];',
  'function gtag(){dataLayer.push(arguments);}',
  "gtag('consent','default',{",
  "'ad_storage':'denied',",
  "'ad_user_data':'denied',",
  "'ad_personalization':'denied',",
  "'analytics_storage':'denied',",
  "'functionality_storage':'granted',",
  "'personalization_storage':'denied',",
  "'security_storage':'granted',",
  "'wait_for_update':500",
  '});',
  "gtag('set','ads_data_redaction',true);",
  "gtag('set','url_passthrough',false);",
].join('');

/** Build the seven-signal record for a user decision. */
export function buildConsentSignal(value: ConsentValue): ConsentSignal {
  return {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
    functionality_storage: 'granted',
    personalization_storage: value,
    security_storage: 'granted',
  };
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches Google's published signature
    gtag?: (...args: any[]) => void;
  }
}

/**
 * Ensure `dataLayer` and `gtag` exist on `window`. Normally the init script in
 * `<head>` has already created both; this fallback keeps `updateConsent`
 * robust in tests (where SSR injects no script) and in any edge case where
 * the init script failed to execute.
 */
function ensureGtag(): void {
  if (typeof window === 'undefined') return;
  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = [];
  }
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]): void {
      window.dataLayer?.push(args);
    };
  }
}

/**
 * Push a `gtag('consent', 'update', …)` call with the seven-signal record for
 * the given user decision.
 *
 * No-op on the server. Idempotent: can be called multiple times — Google's
 * Consent Mode simply takes the most recent signal.
 */
export function updateConsent(value: ConsentValue): void {
  if (typeof window === 'undefined') return;
  ensureGtag();
  const signals = buildConsentSignal(value);
  window.gtag?.('consent', 'update', signals);
}
